import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActivityIndicator, Text, View } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";

import { ItemSeparator } from "@/shared/components/ui/item-separator";
import { ScreenContainer } from "@/shared/components/ui/screen-container";
import { SearchInput } from "@/shared/components/ui/search-input";
import { EventCard } from "@/features/events/components/event-card";
import { UserListItem } from "@/features/profile/components/user-list-item";
import { useSearchEvents } from "@/features/events/api/events";
import { useSearchProfiles } from "@/features/profile/api/profile";
import type { FollowProfile } from "@/features/profile/api/followers";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import type { Event } from "@lezz/shared";
import { posthog } from "@/shared/lib/posthog";
import { useTabBarHeight } from "@/shared/hooks/use-tab-bar-height";

type SearchListItem =
  | { type: "header"; key: string; title: string }
  | { type: "profile"; key: string; profile: FollowProfile }
  | { type: "event"; key: string; event: Event; isPast: boolean };

export default function SearchScreen() {
  const router = useRouter();
  const [input, setInput] = useState("");
  const { q: query = "" } = useLocalSearchParams<{ q?: string }>();
  const debouncedQuery = useDebouncedValue(query, 350);
  const eventsQuery = useSearchEvents(debouncedQuery);
  const profilesQuery = useSearchProfiles(debouncedQuery);
  const mutedForeground = useCSSVariable("--color-muted-foreground") as string;
  const tabBarHeight = useTabBarHeight();

  const hasQuery = debouncedQuery.trim().length > 0;
  const isLoading = eventsQuery.isLoading || profilesQuery.isLoading;
  // A single failed section just doesn't render; only surface the error
  // when there's nothing useful to show at all.
  const bothFailed = Boolean(eventsQuery.error) && Boolean(profilesQuery.error);

  const profiles = useMemo(
    () => profilesQuery.data ?? [],
    [profilesQuery.data]
  );
  const upcoming = useMemo(
    () => eventsQuery.data?.upcoming ?? [],
    [eventsQuery.data]
  );
  const past = useMemo(() => eventsQuery.data?.past ?? [], [eventsQuery.data]);

  const items = useMemo<SearchListItem[]>(() => {
    const list: SearchListItem[] = [];
    if (profiles.length > 0) {
      list.push({ type: "header", key: "h-profiles", title: "Perfis" });
      list.push(
        ...profiles.map((profile) => ({
          type: "profile" as const,
          key: `p-${profile.userId}`,
          profile,
        }))
      );
    }
    if (upcoming.length > 0) {
      list.push({ type: "header", key: "h-events", title: "Eventos" });
      list.push(
        ...upcoming.map((event) => ({
          type: "event" as const,
          key: event.eventId,
          event,
          isPast: false,
        }))
      );
    }
    if (past.length > 0) {
      list.push({ type: "header", key: "h-past", title: "Eventos passados" });
      list.push(
        ...past.map((event) => ({
          type: "event" as const,
          key: event.eventId,
          event,
          isPast: true,
        }))
      );
    }
    return list;
  }, [profiles, upcoming, past]);

  const showInitialState = !hasQuery;
  const showEmptyState =
    hasQuery && !isLoading && !bothFailed && items.length === 0;
  const showResultList = hasQuery && !isLoading && items.length > 0;

  const lastTrackedQuery = useRef<string>("");
  useEffect(() => {
    if (hasQuery && !isLoading && lastTrackedQuery.current !== debouncedQuery) {
      lastTrackedQuery.current = debouncedQuery;
      const totalCount = profiles.length + upcoming.length + past.length;
      posthog?.capture("event_searched", {
        query: debouncedQuery,
        results_count: totalCount,
        events_count: upcoming.length,
        past_events_count: past.length,
        profiles_count: profiles.length,
        has_results: totalCount > 0,
      });
    }
  }, [hasQuery, isLoading, debouncedQuery, profiles, upcoming, past]);

  const renderItem = useCallback(({ item }: { item: SearchListItem }) => {
    if (item.type === "header") {
      return (
        <Text className="text-muted-foreground mt-4 mb-1 text-sm font-semibold">
          {item.title}
        </Text>
      );
    }
    if (item.type === "profile") {
      return (
        <UserListItem
          userId={item.profile.userId}
          name={item.profile.displayName ?? item.profile.username}
          username={item.profile.username}
          avatarUrl={item.profile.avatarUrl}
        />
      );
    }
    if (item.isPast) {
      return (
        <View className="opacity-60">
          <EventCard event={item.event} variant="compact" />
        </View>
      );
    }
    return <EventCard event={item.event} variant="compact" />;
  }, []);

  const handleSearch = (text: string) => {
    setInput(text);
    router.setParams({ q: text });
  };

  const handleClear = () => {
    setInput("");
    router.setParams({ q: "" });
  };

  return (
    <>
      <Stack.Screen options={{ title: "Pesquisar", headerShown: false }} />
      <ScreenContainer bottomInset={false}>
        <View className="px-5 pt-4 pb-3">
          <SearchInput
            placeholder="Procure por eventos ou perfis..."
            value={input}
            onChange={handleSearch}
            onClear={handleClear}
            accessibilityLabel="Pesquisar eventos ou perfis"
          />
        </View>

        {showInitialState && (
          <View
            className="flex-1 items-center justify-center"
            style={{ paddingBottom: tabBarHeight }}
          >
            <Ionicons name="search-outline" size={48} color={mutedForeground} />
            <Text className="text-muted-foreground mt-3 text-center text-base">
              Pesquise por um evento ou perfil para começar
            </Text>
          </View>
        )}

        {hasQuery && (
          <View className="flex-1 px-5">
            {isLoading && (
              <ActivityIndicator
                size="large"
                className="text-brand mt-6"
                accessibilityLabel="Carregando resultados"
              />
            )}

            {!isLoading && bothFailed && (
              <Text
                className="text-destructive mt-5 text-center"
                accessibilityRole="alert"
                accessibilityLiveRegion="polite"
              >
                Falha ao buscar resultados
              </Text>
            )}

            {showEmptyState && (
              <Text className="text-muted-foreground mt-5 text-center">
                {`Nenhum resultado encontrado para "${debouncedQuery}". Tente outras palavras-chave.`}
              </Text>
            )}

            {showResultList && (
              <FlashList
                data={items}
                keyExtractor={(item) => item.key}
                getItemType={(item) => item.type}
                renderItem={renderItem}
                contentContainerStyle={{ paddingBottom: tabBarHeight }}
                contentInsetAdjustmentBehavior="automatic"
                showsVerticalScrollIndicator={false}
                ItemSeparatorComponent={ItemSeparator}
              />
            )}
          </View>
        )}
      </ScreenContainer>
    </>
  );
}
